import React from 'react';
import PropTypes from 'prop-types';
import { TablePagination } from '@mui/material';
import TablePaginationActions from './TablePaginationActions';

/**
 * Modern Responsive Data Table Component
 * - Desktop: Clean table with no horizontal scroll
 * - Mobile: Card-based layout for better UX
 * 
 * Eliminates the outdated horizontal scrolling pattern
 */
export default function ResponsiveDataTable({
  columns,
  data,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  renderActions,
  emptyMessage = 'Aucune donnée disponible',
  keyField = 'id',
}) {
  const paginatedData = rowsPerPage > 0
    ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : data;

  return (
    <div className="w-full">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-hidden">
        <div className="bg-white rounded-lg">
          {/* Table Header */}
          <div 
            className="grid gap-4 py-3 px-4 border-b border-gray-100 bg-gray-50 rounded-t-lg"
            style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
          >
            {columns.map((col, idx) => (
              <div 
                key={idx} 
                className={`text-sm font-semibold text-gray-600 ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'}`}
              >
                {col.header}
              </div>
            ))}
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-100">
            {paginatedData.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                {emptyMessage}
              </div>
            ) : (
              paginatedData.map((row, rowIdx) => (
                <div 
                  key={row[keyField] || rowIdx}
                  className="grid gap-4 py-4 px-4 hover:bg-gray-50 transition-colors items-center"
                  style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
                >
                  {columns.map((col, colIdx) => (
                    <div 
                      key={colIdx}
                      className={`text-sm text-gray-700 ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'} ${col.truncate ? 'truncate' : ''}`}
                    >
                      {col.render ? col.render(row, rowIdx) : row[col.field]}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4">
        {paginatedData.length === 0 ? (
          <div className="py-12 text-center text-gray-500 bg-white rounded-xl shadow-sm">
            {emptyMessage}
          </div>
        ) : (
          paginatedData.map((row, rowIdx) => (
            <div 
              key={row[keyField] || rowIdx}
              className="bg-white rounded-xl shadow-md border border-gray-200 p-4 space-y-3 hover:shadow-lg transition-shadow"
            >
              {columns.map((col, colIdx) => {
                // Skip action column in the main card body - render it separately
                if (col.isAction) return null;
                
                return (
                  <div key={colIdx} className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-3">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {col.header}
                    </span>
                    <span className="text-sm text-gray-900 font-medium break-words">
                      {col.render ? col.render(row, rowIdx) : row[col.field]}
                    </span>
                  </div>
                );
              })}
              
              {/* Actions at bottom of card */}
              {renderActions && (
                <div className="pt-3 mt-2 border-t border-gray-200">
                  {renderActions(row, rowIdx)}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {data.length > 0 && (
        <div className="mt-6 flex justify-center md:justify-end">
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 25, { label: 'Tout', value: -1 }]}
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            labelRowsPerPage="Lignes par page"
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            ActionsComponent={TablePaginationActions}
            sx={{
              '.MuiTablePagination-root': {
                overflow: 'visible',
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                marginBottom: 0,
              },
              '.MuiTablePagination-select': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
              '.MuiTablePagination-toolbar': {
                minHeight: { xs: '48px', sm: '52px' },
                paddingLeft: { xs: '8px', sm: '16px' },
                paddingRight: { xs: '8px', sm: '16px' },
              },
            }}
          />
        </div>
      )}
    </div>
  );
}

ResponsiveDataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string.isRequired,
    field: PropTypes.string,
    render: PropTypes.func,
    align: PropTypes.oneOf(['left', 'center', 'right']),
    truncate: PropTypes.bool,
    isAction: PropTypes.bool,
  })).isRequired,
  data: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  renderActions: PropTypes.func,
  emptyMessage: PropTypes.string,
  keyField: PropTypes.string,
};
